/* eslint-disable react/no-unescaped-entities */
import { FileText, CheckCircle2, AlertTriangle, Calendar, Wallet, ClipboardCheck, Building2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import GermanyResourceShell from './_components/GermanyResourceShell';

const TITLE = 'APS Certificate Germany 2026 | Requirements, Process & Fees';
const DESC  = 'APS Certificate guide for Indian students: who needs it, document checklist, online registration, interview process, fees (₹18,500) and 4–8 week timeline.';

const APS_DOCS = [
  '10th & 12th marksheets (originals + 2 certified copies)',
  'Bachelor degree certificate + provisional certificate',
  'Consolidated marksheet OR all semester marksheets',
  'Migration certificate (from your last university)',
  'University admission letter (if available — optional but accelerates verification)',
  'Valid passport (data page copy)',
  '4 recent passport-size photos',
  'APS application form (downloaded from aps-india.de, signed)',
  'Demand draft / online payment receipt of ₹18,500',
];

const APS_STEPS = [
  { step: 1, title: 'Register on APS-India portal',         body: 'Create an account at aps-india.de — German Embassy New Delhi runs this for all Indian students.' },
  { step: 2, title: 'Pay the ₹18,500 verification fee',      body: 'Demand draft favouring "Embassy of the Federal Republic of Germany, New Delhi" OR online via the APS portal.' },
  { step: 3, title: 'Upload + courier originals',           body: 'Soft copies uploaded online; hard copies couriered to APS office, New Delhi within 14 days.' },
  { step: 4, title: 'Interview (in-person OR Zoom)',        body: 'Subject-knowledge interview testing the depth of your bachelor degree. ~25 minutes; English/Hindi.' },
  { step: 5, title: 'Verification & certificate issuance',  body: 'APS verifies marksheets directly with your university. Approved certificate issued 4–8 weeks later.' },
  { step: 6, title: 'Use APS for visa & admissions',        body: 'Upload the digital APS certificate during university application + at the German embassy for your student visa.' },
];

const COMMON_MISTAKES = [
  'Submitting attested photocopies instead of certified copies — auto-rejected.',
  'Missing migration certificate — biggest single delay reason.',
  'Booking interview before documents are verified — slot wasted.',
  'Sending the demand draft to the wrong embassy unit — adds 2–3 weeks.',
  'Underestimating subject-knowledge interview difficulty — practice 2–3 weeks in advance.',
];

const FAQS = [
  { q: 'Who needs an APS Certificate for Germany?',
    a: 'Every Indian citizen applying for a Bachelor, Masters or Diploma at a German university must hold an APS certificate. Exemptions exist only for IB diploma holders, PhD candidates, and EU government-scholarship recipients (DAAD, Erasmus+). The certificate is verified once and is valid for life.' },
  { q: 'How much does APS Certificate cost in 2026?',
    a: 'The verification fee is ₹18,500 (paid as demand draft to the German Embassy, New Delhi, or online via the APS-India portal). Re-verification of new degrees costs ₹13,000.' },
  { q: 'How long does APS Certificate take?',
    a: 'Standard timeline is 4–8 weeks from interview completion to certificate dispatch. If your university takes long to verify marksheets directly with APS, this can stretch to 10–12 weeks. We recommend applying at least 4 months before your visa filing.' },
  { q: 'Can I apply for the Germany student visa without APS?',
    a: 'No. APS is mandatory at both the university admission stage and visa filing stage for Indian nationals. The German embassy will reject the visa if APS is missing — there are no exceptions for general degree pathways.' },
  { q: 'What documents are checked during the APS interview?',
    a: 'Your bachelor degree marksheets, syllabus, project work, and core subject knowledge. The 25-minute interview is conducted by a German academic and is in English. Expect questions on your final-year project, core subjects, and any gaps in your academic record.' },
  { q: 'Do I need APS for an Ausbildung programme?',
    a: 'No. APS is required only for academic university programmes (Bachelor / Masters / PhD). Ausbildung (dual vocational training) does NOT require APS verification.' },
];

const GermanyAPSCertificatePage = () => (
  <GermanyResourceShell
    slug="aps-certificate"
    breadcrumbLabel="APS Certificate"
    articleHeadline="APS Certificate Germany — Complete Guide for Indian Students"
    articleDescription={DESC}
    faqs={FAQS}
    related={['blocked-account', 'student-visa', 'cost-of-study']}
  >
    {/* Hero */}
    <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)' }}>
      <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-20">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-5">
            <FileText className="w-3.5 h-3.5 text-[#C5A028]" />
            <span className="text-xs text-white/90 font-medium">Germany Resource — APS Certificate</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-syne text-white leading-tight mb-4">
            APS Certificate Germany 2026 — Requirements, Process &amp; Timeline
          </h1>
          <p className="text-base lg:text-lg text-slate-300 leading-relaxed mb-6">
            APS (Akademische Prüfstelle) is the academic verification certificate every Indian student must hold before applying to a German university. This is the complete 2026 guide — eligibility, ₹18,500 fee structure, 9-document checklist, 6-step process, interview prep, and the 5 mistakes that delay 60% of first-time applicants.
          </p>
          <div className="grid grid-cols-3 gap-3 max-w-xl">
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Fee</div>
              <div className="text-base font-bold text-[#C5A028]">₹18,500</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Timeline</div>
              <div className="text-base font-bold text-[#C5A028]">4–8 weeks</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Validity</div>
              <div className="text-base font-bold text-[#C5A028]">Lifetime</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* What is APS */}
    <section className="py-14 lg:py-18 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-5">What is the APS Certificate?</h2>
        <p className="text-[15px] leading-7 text-slate-700 mb-4">
          APS — short for <em>Akademische Prüfstelle</em> — is the official academic-credential verification body operated jointly by the German Embassy New Delhi, DAAD, and the German Academic Exchange Service. Established in 2007 to combat document fraud, APS verifies the authenticity of every Indian degree and marksheet directly with the issuing university before a German institution accepts your application.
        </p>
        <p className="text-[15px] leading-7 text-slate-700 mb-4">
          A successful APS verification produces a digital + physical certificate that is <strong>valid for life</strong>. You upload it during your German university application and re-use it later for your student visa at the German embassy. Without an APS certificate, no German public or private university will issue an admission letter to an Indian national.
        </p>
        <div className="bg-blue-50 border-l-4 border-[#003087] p-4 my-6 rounded-r-lg">
          <div className="text-xs font-bold text-[#003087] uppercase tracking-wide mb-1">Key Takeaway</div>
          <p className="text-sm text-slate-700">
            APS is the single biggest unlock for studying in Germany as an Indian. Apply 4 months before your visa filing, not at the last minute.
          </p>
        </div>
      </div>
    </section>

    {/* Who needs it */}
    <section className="py-14 lg:py-18 bg-slate-50">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-5">Who Needs APS &amp; Who is Exempt</h2>
        <div className="grid md:grid-cols-2 gap-5">
          <div className="bg-white border border-emerald-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <h3 className="text-base font-bold text-emerald-700 font-syne">REQUIRED FOR</h3>
            </div>
            <ul className="space-y-2 text-sm text-slate-700">
              <li>• All Indian Bachelors, Masters &amp; PhD applicants</li>
              <li>• Diploma + post-graduate diploma applicants</li>
              <li>• Indian-passport holders studying outside India (e.g. Dubai, Singapore)</li>
              <li>• Applicants for German government scholarship programmes (non-DAAD)</li>
            </ul>
          </div>
          <div className="bg-white border border-amber-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <h3 className="text-base font-bold text-amber-700 font-syne">EXEMPT FROM APS</h3>
            </div>
            <ul className="space-y-2 text-sm text-slate-700">
              <li>• International Baccalaureate (IB) Diploma holders</li>
              <li>• PhD applicants with confirmed funding (case-by-case)</li>
              <li>• EU scholarship recipients (DAAD, Erasmus+ ICM)</li>
              <li>• Indian-degree holders entering Ausbildung programmes</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    {/* Documents */}
    <section className="py-14 lg:py-18 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-5">Document Checklist (9 items)</h2>
        <p className="text-sm text-slate-600 mb-6">All documents must be uploaded online AND couriered as certified hard copies within 14 days of online submission. Self-attested photocopies are rejected automatically.</p>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
          <ol className="space-y-3">
            {APS_DOCS.map((doc, i) => (
              <li key={i} className="flex items-start gap-3 text-[15px] text-slate-700">
                <span className="w-6 h-6 rounded-full bg-[#003087] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                <span>{doc}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>

    {/* 6-step process */}
    <section className="py-14 lg:py-18 bg-slate-50">
      <div className="max-w-5xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-7">6-Step APS Verification Process</h2>
        <div className="space-y-3">
          {APS_STEPS.map((s) => (
            <div key={s.step} className="bg-white border border-slate-200 rounded-xl p-5 flex gap-5">
              <div className="w-12 h-12 rounded-xl bg-[#003087] text-white font-bold font-syne text-lg flex items-center justify-center shrink-0">
                {s.step}
              </div>
              <div className="flex-1">
                <h3 className="text-base font-bold font-syne text-slate-900 mb-1.5">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Fee table */}
    <section className="py-14 lg:py-18 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-5">Fee Structure (2026)</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
            <thead className="bg-[#003087] text-white">
              <tr>
                <th className="px-4 py-3 text-left font-syne">Service</th>
                <th className="px-4 py-3 text-left font-syne">Fee (INR)</th>
                <th className="px-4 py-3 text-left font-syne">Mode</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              <tr><td className="px-4 py-3 font-medium">First-time APS verification</td><td className="px-4 py-3">₹18,500</td><td className="px-4 py-3 text-slate-600">DD or online</td></tr>
              <tr><td className="px-4 py-3 font-medium">Re-verification (new degree)</td><td className="px-4 py-3">₹13,000</td><td className="px-4 py-3 text-slate-600">DD or online</td></tr>
              <tr><td className="px-4 py-3 font-medium">Duplicate certificate</td><td className="px-4 py-3">₹2,500</td><td className="px-4 py-3 text-slate-600">Online only</td></tr>
              <tr><td className="px-4 py-3 font-medium">Express interview slot (peak season)</td><td className="px-4 py-3">+ ₹5,000</td><td className="px-4 py-3 text-slate-600">Limited availability</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    {/* Common mistakes */}
    <section className="py-14 lg:py-18 bg-amber-50/40">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-5">5 Mistakes That Delay 60% of First-Time Applicants</h2>
        <div className="space-y-3">
          {COMMON_MISTAKES.map((m, i) => (
            <div key={i} className="bg-white border border-amber-200 rounded-xl p-4 flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-[15px] text-slate-700 leading-7">{m}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Interview Preparation */}
    <section className="py-14 lg:py-18 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-3">Interview Preparation — What to Expect</h2>
        <p className="text-[15px] leading-7 text-slate-700 mb-6">
          The 25-minute APS interview is the single highest-rejection-risk step. It is conducted by a German academic (sometimes a former university professor) and tests whether your bachelor degree matches what is claimed on your marksheets. The interviewer is friendly but rigorous — preparation matters.
        </p>
        <div className="grid md:grid-cols-2 gap-5 mb-6">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
            <h3 className="text-base font-bold font-syne text-[#003087] mb-3">Format &amp; Logistics</h3>
            <ul className="space-y-2 text-sm text-slate-700">
              <li>• Duration: ~25 minutes (can extend to 40)</li>
              <li>• Language: English or Hindi (your choice)</li>
              <li>• Mode: In-person at APS Delhi office OR Zoom</li>
              <li>• No notes or reference material allowed</li>
              <li>• Recorded for verification audit trail</li>
            </ul>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
            <h3 className="text-base font-bold font-syne text-[#003087] mb-3">Topics Commonly Tested</h3>
            <ul className="space-y-2 text-sm text-slate-700">
              <li>• Core final-year project — methodology + result</li>
              <li>• Major subjects in your last 2 semesters</li>
              <li>• Why you chose your bachelor specialisation</li>
              <li>• Academic gaps (year drops, transfers) — be honest</li>
              <li>• Why a Masters in Germany now, not in India</li>
            </ul>
          </div>
        </div>
        <div className="bg-blue-50 border-l-4 border-[#003087] p-4 rounded-r-lg">
          <div className="text-xs font-bold text-[#003087] uppercase tracking-wide mb-1">ZAFCO Interview Tip</div>
          <p className="text-sm text-slate-700">
            Prepare a 2-page "subject summary" of your final-year syllabus — write 3 bullet points per major subject. Re-read it daily for 2 weeks before the interview. Indian students who do this clear the interview at a ~95% rate on their first attempt.
          </p>
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className="py-14 lg:py-18 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-7">Frequently Asked Questions</h2>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <details key={i} className="group bg-slate-50 border border-slate-200 rounded-xl p-5">
              <summary className="font-syne font-semibold text-slate-900 cursor-pointer text-[15px] flex items-start justify-between">
                <span className="pr-6">{f.q}</span>
                <span className="text-[#003087] group-open:rotate-45 transition-transform text-xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-sm leading-7 text-slate-700">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  </GermanyResourceShell>
);

export default GermanyAPSCertificatePage;
